"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import { SHIMMER_BLUR_DATA_URL } from "@/lib/constants";

const DEFAULT_SLIDE_SECONDS = 5;

export interface HeroSlide {
  url: string;
  /** Segundos que o slide fica visível (default 5). */
  duracaoSegundos?: number;
}

interface HeroCarouselProps {
  slides: HeroSlide[];
}

export function HeroCarousel({ slides }: HeroCarouselProps) {
  const [current, setCurrent] = useState(0);
  const images = slides.map((s) => s.url);

  const currentSeconds =
    slides[current]?.duracaoSegundos ?? DEFAULT_SLIDE_SECONDS;

  useEffect(() => {
    if (slides.length <= 1) return;
    // Respeita prefers-reduced-motion: não auto-avança (mostra a 1ª foto fixa).
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    // setTimeout (não setInterval): cada slide tem a própria duração.
    const id = setTimeout(
      () => setCurrent((prev) => (prev + 1) % slides.length),
      Math.max(1, currentSeconds) * 1000,
    );
    return () => clearTimeout(id);
  }, [current, currentSeconds, slides.length]);

  if (images.length === 0) {
    return <div className="hero-pattern absolute inset-0" aria-hidden="true" />;
  }

  return (
    <>
      {images.map((url, i) => (
        <div
          key={url}
          className="absolute inset-0 transition-opacity duration-1000"
          style={{ opacity: i === current ? 1 : 0 }}
          aria-hidden="true"
        >
          <Image
            src={url}
            alt=""
            fill
            className="object-cover"
            priority={i === 0}
            loading={i === 0 ? "eager" : "lazy"}
            fetchPriority={i === 0 ? "high" : "low"}
            sizes="100vw"
            placeholder="blur"
            blurDataURL={SHIMMER_BLUR_DATA_URL}
          />
        </div>
      ))}
      {/* Dark overlay for text legibility */}
      <div className="absolute inset-0 bg-navy-900/60" aria-hidden="true" />
    </>
  );
}
